<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useFollowUpTemplatesStore } from 'dashboard/stores/followUpTemplates';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import FollowUpTemplatesEmptyState from 'dashboard/components-next/Crm/EmptyState/FollowUpTemplatesEmptyState.vue';

const store = useFollowUpTemplatesStore();
const pipelinesStore = usePipelinesStore();
const router = useRouter();

const newDialog = ref(null);
const deleteDialogRef = ref(null);
const selectedTemplateForDelete = ref(null);
const creating = ref(false);
const draft = ref({
  name: '',
  anchor_kind: 'lead_qualified',
  pipeline_id: null,
});

onMounted(() => {
  store.fetchAll();
  // pipelines carregados sob demanda - necessários para o select do dialog
  pipelinesStore.fetchAll();
});

const canSubmit = computed(() => draft.value.name.trim().length >= 2);

// Opções fixas para o gatilho do modelo.
const anchorKindOptions = [
  {
    value: 'lead_qualified',
    label: 'Ao qualificar o lead (IA aplica automaticamente)',
  },
  { value: 'manual_apply', label: 'Aplicação manual pelo agente' },
];

// Pipelines dinâmicos: opção "Global" + lista do store.
const pipelineOptions = computed(() => [
  { value: null, label: 'Global - disponível para todos os pipelines' },
  ...pipelinesStore.list.map(p => ({ value: p.id, label: p.name })),
]);

function openNew() {
  draft.value = { name: '', anchor_kind: 'lead_qualified', pipeline_id: null };
  newDialog.value?.open();
}

function closeDlg() {
  newDialog.value?.close();
}

async function confirmCreate() {
  if (!canSubmit.value || creating.value) return;
  creating.value = true;
  try {
    const created = await store.create({
      name: draft.value.name.trim(),
      anchor_kind: draft.value.anchor_kind,
      pipeline_id: draft.value.pipeline_id || null,
      active: true,
    });
    closeDlg();
    useAlert('Modelo criado.');
    router.push({
      name: 'settings_follow_up_template_edit',
      params: { id: created.id },
    });
  } catch (e) {
    // Mensagem do backend (ex: "Slug já está em uso") vence o fallback genérico.
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.response?.data?.errors?.join?.(', ') ||
      'Falha ao criar modelo.';
    useAlert(msg);
  } finally {
    creating.value = false;
  }
}

function onClickEdit(template) {
  router.push({
    name: 'settings_follow_up_template_edit',
    params: { id: template.id },
  });
}

function onClickDelete(template) {
  // Backend retorna 422 quando o template está em uso em leads ativos.
  // Modelos sem uso fazem soft-delete (active=false) - caller deve avisar
  // que itens "inativos" continuam disponíveis no histórico.
  selectedTemplateForDelete.value = template;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  const template = selectedTemplateForDelete.value;
  if (!template) return;
  try {
    await store.destroy(template.id);
    useAlert('Modelo excluído.');
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.errors?.join?.(', ') ||
        'Falha ao excluir.'
    );
  } finally {
    deleteDialogRef.value?.close();
    selectedTemplateForDelete.value = null;
  }
}

function anchorLabel(anchor) {
  return (
    {
      lead_qualified: 'Ao qualificar',
      manual_apply: 'Aplicação manual',
      stage_entered: 'Entrada em stage',
      lead_created: 'Criação do lead',
    }[anchor] || anchor
  );
}
</script>

<template>
  <SettingsLayout
    :is-loading="store.loading"
    loading-message="Carregando modelos..."
  >
    <template #header>
      <BaseSettingsHeader
        title="Modelos de Follow-up"
        description="Playbooks de toques sequenciais (ligação, e-mail, WhatsApp, visita) aplicados aos leads. Cada lead adota um modelo; a IA escolhe automaticamente quando configurado, ou o agente seleciona manualmente."
        feature-name="follow_up_templates"
      >
        <template v-if="store.list.length" #count>
          <span class="text-body-main text-n-slate-11"
            >{{ store.list.length }} modelo(s)</span
          >
        </template>
        <template #actions>
          <NextButton
            icon="i-lucide-plus"
            size="sm"
            label="Novo modelo"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-2 pt-2 pb-10">
        <FollowUpTemplatesEmptyState v-if="!store.list.length">
          <template #actions>
            <NextButton
              icon="i-lucide-plus"
              label="Criar primeiro modelo"
              @click="openNew"
            />
          </template>
        </FollowUpTemplatesEmptyState>

        <ol v-else class="flex flex-col gap-2">
          <li
            v-for="tpl in store.list"
            :key="tpl.id"
            class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors px-4 py-3 flex items-center gap-4 cursor-pointer"
            @click="onClickEdit(tpl)"
          >
            <span
              class="size-10 rounded-full flex items-center justify-center flex-shrink-0 bg-n-brand/10"
            >
              <i class="i-lucide-list-checks size-5 text-n-brand" />
            </span>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <p class="text-heading-3 text-n-slate-12 truncate mb-0">
                  {{ tpl.name }}
                </p>
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-alpha-2 text-label-small font-medium text-n-slate-11 uppercase tracking-wide"
                  >v{{ tpl.version }}</span
                >
                <span
                  v-if="!tpl.active"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-alpha-2 text-label-small font-medium text-n-slate-11 uppercase tracking-wide"
                  >inativo</span
                >
              </div>
              <p
                v-if="tpl.description"
                class="text-body-small text-n-slate-11 mt-0.5 mb-0 line-clamp-1"
              >
                {{ tpl.description }}
              </p>
              <div
                class="flex items-center gap-4 mt-1 text-body-small text-n-slate-10"
              >
                <span class="inline-flex items-center gap-1">
                  <i class="i-lucide-list size-3" />
                  {{ tpl.steps?.length || 0 }} passos
                </span>
                <span class="inline-flex items-center gap-1">
                  <i class="i-lucide-anchor size-3" />
                  {{ anchorLabel(tpl.anchor_kind) }}
                </span>
                <span
                  v-if="tpl.pipeline_id"
                  class="inline-flex items-center gap-1"
                >
                  <i class="i-lucide-kanban size-3" />
                  pipeline #{{ tpl.pipeline_id }}
                </span>
                <span v-else class="inline-flex items-center gap-1">
                  <i class="i-lucide-globe size-3" />
                  global
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-pen-square"
                label="Editar"
                @click.stop="onClickEdit(tpl)"
              />
              <NextButton
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                v-tooltip="'Excluir modelo'"
                @click.stop="onClickDelete(tpl)"
              />
            </div>
          </li>
        </ol>
      </div>
    </template>

    <Dialog
      ref="newDialog"
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-4">
        <div>
          <h3 class="text-heading-3 text-n-slate-12 mb-1">
            Novo modelo de Follow-up
          </h3>
          <p class="text-body-small text-n-slate-11 mb-0">
            Defina o nome e o gatilho. Você poderá adicionar passos (ligação,
            e-mail, WhatsApp) na próxima etapa.
          </p>
        </div>

        <Input
          id="follow-up-template-name"
          v-model="draft.name"
          label="Nome"
          placeholder="Ex: Vendas Consultivo"
          autofocus
        />

        <div>
          <label class="block text-body-small font-medium text-n-slate-12 mb-1"
            >Gatilho</label
          >
          <SelectInput
            v-model="draft.anchor_kind"
            :options="anchorKindOptions"
            class="w-full [&>select]:w-full"
          />
        </div>

        <div>
          <label class="block text-body-small font-medium text-n-slate-12 mb-1">
            Pipeline (opcional)
          </label>
          <SelectInput
            v-model="draft.pipeline_id"
            :options="pipelineOptions"
            class="w-full [&>select]:w-full"
          />
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <NextButton faded slate label="Cancelar" @click="closeDlg" />
          <NextButton
            :is-loading="creating"
            :disabled="!canSubmit"
            label="Criar modelo"
            @click="confirmCreate"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      title="Excluir modelo"
      :description="`Excluir o modelo “${selectedTemplateForDelete?.name}”? Modelos em uso em leads ativos não podem ser excluídos.`"
      confirm-button-label="Excluir"
      cancel-button-label="Cancelar"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
